import { FC, useEffect, useState, useMemo } from "react";
import { Box, Typography, Checkbox, CircularProgress } from "@mui/material";
import { PositionInfo } from "../InterviewAssistant";
import colors from "../../../shared/constants/color";
import { textSM, textXS } from "../../../shared/constants";
import { ProjectStatusTag } from "../components/shared/ProjectStatusTag";
import { PriorityTag } from "../components/shared/PriorityTag";
import { Button } from "../../../components";
import { WorkingModelTag } from "../components/shared/WorkingModelTag";
import { MapPinIcon, UsersIcon } from "../../../shared/images/icons";
import { sanitizeRichText } from "../../../shared/utils/htmlUtils";
import ModalBase from "../../../components/ui/ModalBase";
import axios from "axios";
import { SERVICEURL } from "../../../appconfig";
import { useToast } from "../../../shared/hooks/useToast";
import ReusableTabs from "../../../components/ReusableTabs";
import { useGetRelatedInfo } from "../../../hooks/position/useGetRelatedInfo";
import { DataTable } from "../../../components/shared/CandidateDetailCard/DataTable";
import { DifficultyLevel } from "../../positionDetail/components/tabs/DifficultyLevel";
import PositionCriteria from "../../positionDetail/components/PositionCriteria";
import { useWiserTheme } from "../../../hooks/useWiserTheme";
import {
  getPositionModalGridSx,
  getSidebarIconFilter,
} from "../../../shared/theme/wiserThemeHelpers";
import {
  breakWordSx,
  gridFullWidthSx,
  modalHeaderInfoRowSx,
  checkboxNoPaddingSx,
  centeredMinHeightSx,
  relatedPersonsErrorSx,
  modalHeaderRowSx,
  modalHeaderLeftSx,
  modalHeaderTitleRowSx,
  modalHeaderMetaWrapSx,
  modalHeaderTagsSx,
  getIcon14Style,
  modalHeaderRightSx,
  getCreatedByTextSx,
  positionDetailModalSx,
  positionDetailTabSx,
} from "../style";

const GeneralInfoContent: FC<{
  position: PositionInfo;
}> = ({ position }) => {
  const { darkModeActive } = useWiserTheme();

  return (
    <>
      <Box sx={getPositionModalGridSx(darkModeActive)}>
        <Typography>Proje Türü:</Typography>
        <Typography>{position.projectKindName || ""}</Typography>

        <Typography>Şirket:</Typography>
        <Typography>{position.companyName || ""}</Typography>

        <Typography>İlgili İK:</Typography>
        <Typography>
          {(position.responsibleMembers ?? []).map((rm) => rm.name).join(", ") || ""}
        </Typography>

        <Typography>Müşteri:</Typography>
        <Typography>{position.clientName || ""}</Typography>

        <Typography>Ekip:</Typography>
        <Typography>{position.teamName || ""}</Typography>

        <Typography>Teknik Grup:</Typography>
        <Typography>
          {(position.techGroup ?? []).map((tg) => tg.techGroupName).join(", ") || ""}
        </Typography>

        <Typography>Talep Sayısı:</Typography>
        <Typography>{position.headCount || ""}</Typography>

        <Typography>İl / İlçe:</Typography>
        <Typography>
          {position.location && position.location.length > 0
            ? position.location
                .map((loc: any) => {
                  // Yeni format: cityName ve towns array'i
                  if (loc.cityName) {
                    const towns = loc.towns
                      ?.map((t: any) => t.townName)
                      .filter(Boolean)
                      .join(", ");
                    return towns ? `${loc.cityName} / ${towns}` : loc.cityName;
                  }
                  // Eski format: locationName
                  if (loc.locationName) {
                    return loc.locationName;
                  }
                  // Fallback: Belirtilmemiş
                  return "Belirtilmemiş";
                })
                .filter(Boolean)
                .join(" | ")
            : ""}
        </Typography>

        <Typography>Çalışma Şekli:</Typography>
        <Typography>{position.workingModelName || ""}</Typography>

        <Typography>Öncelik:</Typography>
        <Typography>{position.priorityName || ""}</Typography>

        <Typography>Minimum Deneyim Yılı:</Typography>
        <Typography>{position.minExperienceYear || ""}</Typography>

        <Typography>Müşteri Açıklama:</Typography>
        <Typography>{position.description || ""}</Typography>

        <Typography>IK Not:</Typography>
        <Typography sx={breakWordSx}>
          {position.detailDescription || ""}
        </Typography>

        <Box sx={gridFullWidthSx}>
          <PositionCriteria
            value={{
              criticalCriteria: position.criticalCriteria ?? [],
              mustCriteria: position.mustCriteria ?? [],
              optionalCriteria: position.optionalCriteria ?? [],
            }}
            readOnly
          />
        </Box>

        <Box sx={modalHeaderInfoRowSx}>
          <Checkbox
            checked={position.isPublished}
            size="small"
            sx={checkboxNoPaddingSx}
          />
          <Typography>Websitesinde Yayınla</Typography>
        </Box>
        <Box></Box>

        <Typography>Yayın Açıklama:</Typography>
        <Typography component="div">
          {position.publishDescription ? (
            /*
              GUVENLIK: publishDescription pozisyon formundaki ReactQuill
              editorunden geliyor ve "Yayin Aciklamasi" alani serbest HTML
              kabul ediyor. Sanitize edilmeden basildiginda
              <img src=x onerror=...> pozisyonu GORUNTULEYEN kullanicinin
              oturumunda calisir -- StatusTab notlariyla ayni depolanmis XSS
              sinifi. Upstream burada da sanitizasyon YOK; bilincli sapma.
              Bkz. shared/utils/htmlUtils.ts, NOT_PORTED.md §2.2.
            */
            <span
              dangerouslySetInnerHTML={{
                __html: sanitizeRichText(position.publishDescription),
              }}
            />
          ) : (
            ""
          )}
        </Typography>
      </Box>
    </>
  );
};

const RelatedPersonsContent: FC<{ positionId: string }> = ({ positionId }) => {
  const { darkModeActive, tokens } = useWiserTheme();
  const { data, loading, error } = useGetRelatedInfo(positionId);

  const tableData = useMemo(() => {
    if (!data) return [];
    if (data.data && Array.isArray(data.data)) return data.data;
    if (Array.isArray(data)) return data;
    return [];
  }, [data]);

  const columns = [
    {
      key: "relatedPersonName",
      label: "İlgili Kişi",
      render: (value: string) => (
        <Typography
          sx={textSM(
            400,
            darkModeActive ? tokens.textPrimary : colors.neutral.N800,
          )}
        >
          {value}
        </Typography>
      ),
    },
    {
      key: "email",
      label: "E-Mail",
      render: (value: string) => (
        <Typography sx={textSM(400, darkModeActive ? tokens.link : "#0065FF")}>
          {value}
        </Typography>
      ),
    },
    {
      key: "cellPhone",
      label: "Cep Tel",
      render: (value: string) => (
        <Typography
          sx={textSM(
            400,
            darkModeActive ? tokens.textPrimary : colors.neutral.N800,
          )}
        >
          {value}
        </Typography>
      ),
    },
    {
      key: "workPhone",
      label: "İş Tel",
      render: (value: string) => (
        <Typography
          sx={textSM(
            400,
            darkModeActive ? tokens.textPrimary : colors.neutral.N800,
          )}
        >
          {value}
        </Typography>
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={centeredMinHeightSx}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={relatedPersonsErrorSx}>
        Bilgiler yüklenirken bir hata oluştu.
      </Box>
    );
  }

  return (
    <DataTable
      columns={columns}
      data={tableData}
      emptyMessage="Kayıt bulunamadı."
      hideBorders
      py={2}
    />
  );
};

const PositionHeader: FC<{
  position: PositionInfo;
  formatDate: (date: string | null) => string;
}> = ({ position, formatDate }) => {
  const { darkModeActive, tokens } = useWiserTheme();
  const iconFilter = getSidebarIconFilter(darkModeActive);

  return (
    <>
      {/* Header */}
      <Box sx={modalHeaderRowSx}>
        {/* Header Left Side */}
        <Box sx={modalHeaderLeftSx}>
          <Box sx={modalHeaderTitleRowSx}>
            <Typography
              sx={textSM(
                500,
                darkModeActive ? tokens.textPrimary : colors.neutral.N800,
              )}
            >
              {(position.techGroup ?? []).map((tg) => tg.techGroupName).join(" - ")}
            </Typography>
            <WorkingModelTag workingModel={position.workingModelName || ""} />
          </Box>

          <Box sx={modalHeaderMetaWrapSx}>
            {/* Client and Team */}
            <Typography
              sx={textXS(
                400,
                darkModeActive ? tokens.textSecondary : colors.neutral.N500,
              )}
            >
              {position.clientName?.toTitleCaseTR() || ""} ·{" "}
              {position.teamName?.toTitleCaseTR() || ""}
            </Typography>

            {/* Status and Priority Tags */}
            <Box sx={modalHeaderTagsSx}>
              <ProjectStatusTag status={position.statusName} />
              <PriorityTag priority={position.priorityId} />
            </Box>

            {/* Candidate Count and Location */}
            <Box sx={modalHeaderInfoRowSx}>
              <img
                src={UsersIcon}
                alt=""
                style={getIcon14Style(iconFilter)}
              />
              <Typography
                sx={textXS(
                  400,
                  darkModeActive ? tokens.textSecondary : colors.neutral.N500,
                )}
              >
                Talep : {position.headCount} Kişi
              </Typography>
              <img
                src={MapPinIcon}
                alt=""
                style={getIcon14Style(iconFilter)}
              />
              <Typography
                sx={textXS(
                  400,
                  darkModeActive ? tokens.textSecondary : colors.neutral.N500,
                )}
              >
                {position.location && position.location.length > 0
                  ? position.location
                      .map((loc: any) => {
                        // Yeni format: cityName ve towns array'i
                        if (loc.cityName) {
                          const towns = loc.towns
                            ?.map((t: any) => t.townName)
                            .filter(Boolean)
                            .join(", ");
                          return towns
                            ? `${loc.cityName} (${towns})`
                            : loc.cityName;
                        }
                        // Eski format: locationName
                        if (loc.locationName) {
                          return loc.locationName;
                        }
                        // Fallback: Belirtilmemiş
                        return "Belirtilmemiş";
                      })
                      .filter(Boolean)
                      .join(" | ")
                  : "Belirtilmemiş"}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Header Right Side */}
        <Box sx={modalHeaderRightSx}>
          <Typography
            sx={textXS(
              400,
              darkModeActive ? tokens.textSecondary : colors.neutral.N500,
            )}
          >
            Oluşturulma Tarihi: <b>{formatDate(position.createdAt)}</b>
          </Typography>
          <Typography sx={getCreatedByTextSx(darkModeActive, tokens)}>
            <b>{position.createdBy}</b> tarafından oluşturuldu.
          </Typography>
        </Box>
      </Box>
    </>
  );
};

interface IProps {
  show: boolean;
  onClose: () => void;
  positionId: string | null;
}

const PositionDetailModal: FC<IProps> = ({ show, onClose, positionId }) => {
  const { darkModeActive, tokens } = useWiserTheme();
  const [position, setPosition] = useState<PositionInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const { showToast } = useToast();
  const mytoken = localStorage.getItem("token");

  useEffect(() => {
    if (show && positionId) {
      fetchPositionDetail(positionId);
    } else {
      setPosition(null);
      setTabIndex(0);
    }
  }, [show, positionId]);

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const fetchPositionDetail = async (id: string) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${SERVICEURL}api/Position/GetPositionDetail?id=${id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${mytoken}`,
          },
        },
      );

      const code = response.data.code;
      const message = response.data.message;
      const responseData = response.data.data;

      if (code !== 200 || !responseData) {
        showToast(message, "error");
        setPosition(null);
        return;
      }

      // Location bilgilerini zenginleştir
      if (responseData.location && Array.isArray(responseData.location)) {
        const enrichedLocation = await Promise.all(
          responseData.location.map(async (loc: any) => {
            // Eğer cityName zaten varsa, olduğu gibi kullan
            if (loc.cityName) {
              return loc;
            }

            // Eğer cityId varsa ama cityName yoksa, API'den çek
            if (loc.cityId) {
              try {
                // City listesini çek
                const cityResponse = await axios.get(
                  `${SERVICEURL}api/Data/GetCityList`,
                  {
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${mytoken}`,
                    },
                  },
                );

                const city = cityResponse.data?.data?.find(
                  (c: any) => c.id === loc.cityId,
                );
                const cityName = city?.name || `Şehir (${loc.cityId})`;

                // Town bilgilerini çek
                let towns: any[] = [];
                if (
                  loc.townIds &&
                  Array.isArray(loc.townIds) &&
                  loc.townIds.length > 0
                ) {
                  const townResponse = await axios.get(
                    `${SERVICEURL}api/Data/GetTownListByCity?cityId=${loc.cityId}`,
                    {
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${mytoken}`,
                      },
                    },
                  );

                  const allTowns = townResponse.data?.data || [];
                  towns = loc.townIds
                    .map((townId: number) => {
                      const town = allTowns.find((t: any) => t.id === townId);
                      return town
                        ? { townId: town.id, townName: town.name }
                        : null;
                    })
                    .filter(Boolean);
                }

                return {
                  ...loc,
                  cityName,
                  towns,
                };
              } catch (error) {
                console.error("Location bilgisi alınırken hata:", error);
                return loc;
              }
            }

            return loc;
          }),
        );

        responseData.location = enrichedLocation;
      }

      setPosition({
        ...(responseData as PositionInfo),
        id: String(responseData.id),
      });
    } catch (error: any) {
      showToast(
        error.response?.data?.message ||
          "Pozisyon detayları yüklenirken hata oluştu.",
        "error",
      );
      setPosition(null);
    } finally {
      setLoading(false);
    }
  };

  if (!show || !positionId) return null;

  const formatDate = (date: string | null) => {
    if (!date) return "";
    return new Date(date)
      .toLocaleString("tr-TR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
      .replace(",", "");
  };

  const tabs = [
    {
      label: "Genel Bilgiler",
      content: position ? <GeneralInfoContent position={position} /> : null,
    },
    {
      label: "İlgili Kişiler",
      content: <RelatedPersonsContent positionId={positionId} />,
    },
    {
      label: `Zorluk Derecesi (${position?.diffPoint || "0"})`,
      content: <DifficultyLevel positionId={positionId} readOnly />,
    },
  ];

  return (
    <ModalBase
      open={show}
      onClose={onClose}
      title="Pozisyon Detay"
      maxWidth="md"
      actions={
        <Button color={colors.deepBlue} variant="contained" onClick={onClose}>
          Kapat
        </Button>
      }
      sx={positionDetailModalSx}
    >
      {loading ? (
        <Box sx={centeredMinHeightSx}>
          <CircularProgress />
        </Box>
      ) : !position ? (
        <Box sx={centeredMinHeightSx}>
          <Typography
            sx={textSM(
              400,
              darkModeActive ? tokens.textMuted : colors.neutral.N500,
            )}
          >
            Pozisyon bilgileri yüklenemedi.
          </Typography>
        </Box>
      ) : (
        <>
          <PositionHeader position={position} formatDate={formatDate} />

          <ReusableTabs
            value={tabIndex}
            onChange={handleChangeTab}
            tabs={tabs}
            tabSx={positionDetailTabSx}
          />
        </>
      )}
    </ModalBase>
  );
};

export default PositionDetailModal;
